import { useState } from 'react';
import { ApiClient, CreationResponse } from '../../api/client';
import WebApp from '@twa-dev/sdk';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';

interface SharingOptionsProps {
  creation: CreationResponse;
  // Pass back only the new sharing status
  onUpdate: (isShared: boolean) => void; 
}

export function SharingOptions({ creation, onUpdate }: SharingOptionsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  
  // Toggle between public and private
  const toggleSharing = async () => {
    if (isLoading) return;
    posthog.capture(creation.is_shared ? 'make_creation_private_clicked' : 'make_creation_public_clicked', { creation_id: creation.id });
    try {
      setIsLoading(true);
      
      let updatedCreation: CreationResponse;
      
      if (creation.is_shared) {
        // Make private
        updatedCreation = await ApiClient.makeCreationPrivate(creation.id);
        WebApp.showPopup({ 
          message: t('sharingOptions.makePrivateSuccess'),
          buttons: [{ type: 'ok' }]
        });
      } else {
        // Make public
        updatedCreation = await ApiClient.makeCreationPublic(creation.id);
        WebApp.showPopup({ 
          message: t('sharingOptions.makePublicSuccess'),
          buttons: [{ type: 'ok' }]
        });
      }
      
      // Send back only the updated sharing status
      onUpdate(updatedCreation.is_shared ?? false);
    } catch (error) {
      console.error('Error toggling sharing status:', error);
      WebApp.showPopup({ 
        message: t('sharingOptions.toggleError'),
        buttons: [{ type: 'ok' }]
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Share via Telegram with a link
  const shareViaLink = async () => {
    try {
      setIsLoading(true);
      posthog.capture('share_creation_via_link_clicked', { creation_id: creation.id });
      
      // First, ensure the creation is public before sharing
      let updatedCreation = creation;
      
      // If the creation is not already shared, make it public first
      if (!creation.is_shared) {
        updatedCreation = await ApiClient.makeCreationPublic(creation.id);
        // Update the parent component with the new sharing status
        onUpdate(updatedCreation.is_shared ?? false);
      }
      
      // Use environment variables to create a proper link (similar to SendToTelegramButton)
      const botUsername = import.meta.env.VITE_BOT_USERNAME;
      const miniAppName = import.meta.env.VITE_MINI_APP_NAME;
      const shareUrl = `https://t.me/${botUsername}/${miniAppName}?startapp=creation_${creation.id}`;
      
      // Open Telegram's share dialog
      await WebApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(t('sharingOptions.shareDefaultText'))}`);
    } catch (error) {
      console.error('Error sharing creation:', error);
      WebApp.showPopup({ 
        message: t('sharingOptions.shareError'),
        buttons: [{ type: 'ok' }]
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show the sharing options popup
  const showSharingOptions = () => {
    WebApp.showPopup({
      title: t('sharingOptions.popupTitle'),
      message: creation.is_shared 
        ? t('sharingOptions.popupMessagePublic')
        : t('sharingOptions.popupMessagePrivate'),
      buttons: [
        { 
          id: 'toggle', 
          type: creation.is_shared ? 'default' : 'default',
          text: creation.is_shared ? t('sharingOptions.makePrivateButton') : t('sharingOptions.makePublicButton')
        },
        { 
          id: 'share', 
          type: 'default', 
          text: t('sharingOptions.shareViaTelegramButton')
        },
        { 
          id: 'cancel', 
          type: 'cancel'
        }
      ]
    }, (buttonId) => {
      if (buttonId === 'toggle') {
        toggleSharing();
      } else if (buttonId === 'share') {
        shareViaLink();
      }
    });
  };
  
  return (
    <button
      onClick={showSharingOptions}
      disabled={isLoading}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 text-buttonText hover:bg-buttonHoverBg border border-border shadow-sm text-sm font-medium transition-colors"
      title={t('sharingOptions.mainButtonTitle')}
    >
      <span className="material-symbols-outlined text-base leading-none">
        {creation.is_shared ? 'public' : 'lock'}
      </span>
      <span>{t('sharingOptions.mainButtonText')}</span>
    </button>
  );
} 